/**
 * Connection problems: time out and retry (PRODUCT_DECISIONS, 2026-10-23). The ONE owner for
 * both apps; each app's Supabase client passes its fetch through createResilientFetch().
 *
 * Why: when the database stalls (docs/investigations/2026-10-22-db-timeouts.md, stalls of
 * 1-5 minutes), a browser fetch has no timeout of its own, so a request could wait for minutes
 * and the screen behind it spun the whole time, including "Signing in..." on the login page.
 *
 * What it does, per request:
 *   - READS (GET/HEAD, and POST to an RPC in READ_ONLY_RPCS) time out and are retried twice,
 *     after 1 s and 3 s, on a timeout, a network failure or a 502/503/504. A read cannot
 *     change anything, so repeating it is safe. A 500 carrying 57014 (statement timeout) is
 *     NOT retried: the database already spent its full 8 s on that query and is likely
 *     overloaded, so repeating it adds load (ruling 6, PRODUCT_DECISIONS 2026-11-02).
 *   - This is the ONLY retry layer for Supabase client requests: the contractor app's React
 *     Query does not retry a Supabase error again (apps/contractor-app/src/lib/queryClient.js,
 *     shouldRetryQuery). A request this file does not retry (writes, RPCs outside
 *     READ_ONLY_RPCS) is therefore not retried anywhere.
 *   - WRITES (everything else) time out and are NEVER retried. A write that timed out may
 *     still have committed on the server, so repeating it could create a second invoice or
 *     payment. The caller gets an error whose message says the save could not be
 *     CONFIRMED, not that it failed. Network failures on writes pass through unchanged.
 *   - Requests that must not be cut off pass through with no timeout (see classify()).
 * Status goes out as a window event (CONNECTION_EVENT) for the app's banner, and every
 * request that hit a timeout is reported through the app's runtime error reporter.
 */

export const CONNECTION_EVENT = 'tm:connection';

export const READ_TIMEOUT_MESSAGE = "Couldn't reach the server. Check your connection and try again.";
export const WRITE_TIMEOUT_MESSAGE = "We couldn't confirm this was saved — check before trying again.";

// PostgREST/RPC: the database itself cancels any statement after 8 s (authenticated) or 3 s
// (anon) — pg_roles.rolconfig, verified live 2026-10-23 — so no healthy REST call can need
// more than that plus the network. The slowest app RPC on record (pg_stat_statements,
// 2026-10-23) is get_filtered_timesheets at 7.6 s max. 15 s leaves margin over both.
const REST_TIMEOUT_MS = 15000;
const AUTH_TIMEOUT_MS = 15000;
// Edge functions render PDFs, send email and call Stripe. Supabase's gateway closes a request
// that has produced no response after 150 s, so a call still waiting past that cannot succeed;
// cutting it any earlier could cut off a send that was about to finish.
const FUNCTIONS_TIMEOUT_MS = 155000;
const STORAGE_TIMEOUT_MS = 30000;
const RETRY_DELAYS_MS = [1000, 3000];

// RPCs that cannot write, so a POST to them is a read and may be retried. Every one is
// STABLE (provolatile 's') with no INSERT/UPDATE/DELETE in its body — verified live
// 2026-10-23 against pg_proc for every RPC name called from apps/contractor-app,
// apps/customer-portal and packages/shared-ui. PostgREST runs STABLE functions in a READ ONLY
// transaction as well. An RPC NOT listed here is treated as a write: never retried. Adding a
// name is only safe after re-checking the function is STABLE and writes nothing.
export const READ_ONLY_RPCS = new Set([
  'company_tz',
  'get_calendar_events_filtered',
  'get_customer_page_aggregates',
  'get_customer_scheduling_context',
  'get_customer_scheduling_slots',
  'get_filtered_timesheets',
  'get_messages_for_context',
  'get_public_business_page',
  'get_sms_consent_by_token',
  'is_current_device_active',
  'my_access_status',
  'search_service_tags',
]);

const ERROR_NAME = 'Connection problem';

class ConnectionTimeoutError extends Error {
  constructor(message, isWrite) {
    super(message);
    // postgrest-js turns a thrown fetch error into { message: `${name}: ${message}` }, and
    // most screens show that message as-is, so the name is written to be read by a person:
    // "Connection problem: We couldn't confirm this was saved ...". Deliberately NOT
    // 'AbortError': several screens hide AbortError as "the user cancelled", and a timeout
    // must be seen. No `code` either: UserContext reads a coded error as "the server
    // answered" and would sign the user out on a timeout.
    this.name = ERROR_NAME;
    this.isConnectionTimeout = true;
    this.unconfirmedWrite = Boolean(isWrite);
  }
}

/** True for a timeout from this wrapper, in any of the shapes supabase-js hands it back. */
export function isConnectionTimeout(err) {
  if (!err) return false;
  if (err.isConnectionTimeout || err.name === ERROR_NAME) return true;
  if (err.context && (err.context.isConnectionTimeout || err.context.name === ERROR_NAME)) return true;
  return String(err.message || '').startsWith(`${ERROR_NAME}: `);
}

/** True when a WRITE timed out: it may or may not have been saved. */
export function isUnconfirmedWrite(err) {
  if (!isConnectionTimeout(err)) return false;
  if (err.unconfirmedWrite || err.context?.unconfirmedWrite) return true;
  return String(err.message || '').includes(WRITE_TIMEOUT_MESSAGE);
}

const LARGE_BODY_BYTES = 100 * 1024;
const hasLargeBody = (body) => {
  if (!body) return false;
  if (typeof body === 'string') return body.length > LARGE_BODY_BYTES;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return body.byteLength > LARGE_BODY_BYTES;
  return typeof body.getReader === 'function'; // a stream
};

const sleep = (ms, signal) => new Promise((resolve) => {
  const t = setTimeout(resolve, ms);
  signal?.addEventListener?.('abort', () => { clearTimeout(t); resolve(); }, { once: true });
});

function emit(state, detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(CONNECTION_EVENT, { detail: { state, ...detail } }));
  } catch { /* status signalling must never break a request */ }
}

/**
 * @param {object} opts
 * @param {string} opts.supabaseUrl   requests outside it pass straight through
 * @param {Function} [opts.fetchImpl] defaults to the global fetch, resolved per call so the
 *                                    error reporter's fetch wrapper stays in the chain
 * @param {Function} [opts.report]    (error, extra) => void — the app's errorReporter.report
 */
export function createResilientFetch({ supabaseUrl, fetchImpl, report } = {}) {
  const base = String(supabaseUrl || '').replace(/\/+$/, '');
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  let degraded = false; // a banner is up: the next good response clears it

  const classify = (url, method) => {
    let path = '';
    let search = '';
    try {
      const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
      path = u.pathname;
      search = u.search;
    } catch { path = String(url).split('?')[0]; }

    const isRead = method === 'GET' || method === 'HEAD';
    if (path.startsWith('/rest/v1/rpc/')) {
      const fn = path.slice('/rest/v1/rpc/'.length);
      return { path, timeoutMs: REST_TIMEOUT_MS, isRead: isRead || (method === 'POST' && READ_ONLY_RPCS.has(fn)) };
    }
    if (path.startsWith('/rest/v1/')) return { path, timeoutMs: REST_TIMEOUT_MS, isRead };
    if (path.startsWith('/auth/v1/')) {
      // NO timeout on a token refresh. Refresh tokens are single-use: if the server rotated
      // it and the answer never arrived, the next refresh replays the old token, and after
      // the reuse window that revokes the session — a timeout here would sign the user out.
      if (/grant_type=refresh_token/.test(search)) return null;
      // A password sign-in that timed out saved nothing the user has to check: it is not
      // retried (it is a POST), but it is reported as "couldn't connect", not "unconfirmed".
      return { path, timeoutMs: AUTH_TIMEOUT_MS, isRead, savesNothing: /grant_type=password/.test(search) };
    }
    if (path.startsWith('/functions/v1/')) return { path, timeoutMs: FUNCTIONS_TIMEOUT_MS, isRead };
    if (path.startsWith('/storage/v1/')) {
      // NO timeout on an upload: its length depends on the file size and the user's
      // connection, and a phone on a job site can legitimately take minutes.
      if ((method === 'POST' || method === 'PUT') && /^\/storage\/v1\/object\/(?!sign\/|list\/|move$|copy$|info\/)/.test(path)) return null;
      return { path, timeoutMs: STORAGE_TIMEOUT_MS, isRead };
    }
    return null; // realtime and anything else: unchanged
  };

  const attempt = async (input, init, timeoutMs, callerSignal) => {
    const controller = new AbortController();
    let timedOut = false;
    const onCallerAbort = () => controller.abort(callerSignal.reason);
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort(callerSignal.reason);
      else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
    try {
      // The timer covers sending the request and waiting for the response headers. A
      // PostgREST response is sent once the query has finished, so the body read after this
      // is not cut off. (Large request bodies never get here: see hasLargeBody.)
      return await doFetch(input, { ...(init || {}), signal: controller.signal });
    } catch (err) {
      // Stop listening to the caller only when the request ended without a response. After
      // a response the caller's signal must still cancel the body download, as before.
      if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
      if (timedOut && !callerSignal?.aborted) {
        const e = new Error('timeout');
        e.__timeout = true;
        throw e;
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  };

  const callerAbortError = (signal) => signal.reason || new DOMException('The operation was aborted.', 'AbortError');

  // 57014 (statement timeout, a 500) is deliberately absent: see the header (ruling 6).
  const retryableResponse = (res) => res.status === 502 || res.status === 503 || res.status === 504;

  const sendReport = (outcome, method, path, timeoutMs, attempts) => {
    if (!report) return;
    try {
      report(new Error(`Client timeout (${outcome}): ${method} ${path}`), {
        code: 'CLIENT_TIMEOUT', method, url: path, timeout_ms: timeoutMs, attempts, outcome,
      });
    } catch { /* reporting must never break a request */ }
  };

  const markOk = (res) => {
    if (degraded && res && res.status < 500) {
      degraded = false;
      emit('ok');
    }
  };

  return async function resilientFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url || String(input);
    if (!base || !url.startsWith(base)) return doFetch(input, init);
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    const route = classify(url, method);
    // A request carrying a file or a large body (portal uploads go through the portal-files
    // edge function as FormData; signatures travel as base64) is NOT timed: sending it takes
    // as long as the file and the user's uplink need, and cutting it off would report an
    // upload that may well have arrived as unconfirmed.
    if (!route || hasLargeBody(init?.body)) return doFetch(input, init);

    const { path, timeoutMs, isRead, savesNothing } = route;
    const callerSignal = init?.signal || (typeof input !== 'string' ? input?.signal : undefined);

    if (!isRead) {
      try {
        const res = await attempt(input, init, timeoutMs, callerSignal);
        markOk(res);
        return res;
      } catch (err) {
        if (!err?.__timeout) throw err;
        degraded = true;
        if (savesNothing) {
          emit('failed', { method, path });
          sendReport('failed', method, path, timeoutMs, 1);
          throw new ConnectionTimeoutError(READ_TIMEOUT_MESSAGE, false);
        }
        emit('unconfirmed', { method, path });
        sendReport('unconfirmed_write', method, path, timeoutMs, 1);
        throw new ConnectionTimeoutError(WRITE_TIMEOUT_MESSAGE, true);
      }
    }

    let timeouts = 0;
    for (let i = 0; ; i += 1) {
      const last = i >= RETRY_DELAYS_MS.length;
      try {
        const res = await attempt(input, init, timeoutMs, callerSignal);
        if (!last && retryableResponse(res)) {
          degraded = true;
          emit('retrying', { method, path });
          await sleep(RETRY_DELAYS_MS[i], callerSignal);
          if (callerSignal?.aborted) throw callerAbortError(callerSignal);
          continue;
        }
        if (timeouts) sendReport('recovered', method, path, timeoutMs, i + 1);
        markOk(res);
        if (last && res.status >= 500) { degraded = true; emit('failed', { method, path }); }
        return res;
      } catch (err) {
        if (callerSignal?.aborted) throw err;
        const isTimeout = Boolean(err?.__timeout);
        // fetch rejects with a TypeError when no response arrived at all (offline, reset).
        const isNetwork = !isTimeout && err instanceof TypeError;
        if (!isTimeout && !isNetwork) throw err;
        if (isTimeout) timeouts += 1;
        degraded = true;
        if (last) {
          emit('failed', { method, path });
          if (timeouts) sendReport('failed', method, path, timeoutMs, i + 1);
          if (isTimeout) throw new ConnectionTimeoutError(READ_TIMEOUT_MESSAGE, false);
          throw err;
        }
        emit('retrying', { method, path });
        await sleep(RETRY_DELAYS_MS[i], callerSignal);
        if (callerSignal?.aborted) throw callerAbortError(callerSignal);
      }
    }
  };
}
